/**
 * Singleton providing helper methods relative to date and time format,
 *    - mainly use the moment library [1]
 *    - also moment duration add-on [2]
 *
 * (?) What could be the next library to interface:
 *      - Tempo [3]
 *
 * @link https://momentjs.com/ [1]
 * @link https://github.com/jsmreese/moment-duration-format [2]
 * @link https://tempo.formkit.com [3]
 */
import moment from 'moment/min/moment-with-locales'
import { appendStr, curry, equals, gt, lt, pipe, pipe_, replace, test, toInteger, when } from '~/utils/lib/core/prelude'
/**
 * @typedef {object} DatetimeParseDatetimeOptions
 * @property {string|undefined} [inputFormat=undefined] undefined → ISO 8601
 * @property {string|undefined} [outputFormat=undefined] undefined → ISO 8601
 * @property {boolean} [inputForceUTC0=false] force input value to be
 *    used as if it was an UTC:0 value **EVEN IF** the input format does not
 *    contain any information about timezone/UTC offset - see [1]
 * @property {boolean} [outputConvertToLocal=false] convert output value
 *    datetime to the local timezone/UTC offset - see [2]
 * @property {[import('moment/min/moment-with-locales').DurationInputArg1, import('moment/min/moment-with-locales').DurationInputArg2]} [additional] eg. [5, 'days'] - see [3]
 *
 * @link https://momentjs.com/docs/#/parsing/utc/ [1]
 * @link https://momentjs.com/docs/#/manipulating/local/ [2]
 * @link https://momentjs.com/docs/#/manipulating/add/ [3]
 *
 *
 * @name parseDatetime
 * @desc Parse a stringified date to another one, with format and al. applied
 * @curried
 * @type {import('./datetime_').curry_parseDatetime}
 */
export const parseDatetime = curry((
  /** @type {DatetimeParseDatetimeOptions} */
  {
    inputFormat = undefined,
    inputForceUTC0 = false,
    outputFormat = undefined,
    outputConvertToLocal = false,
    additional = undefined,
  },
  /** @type {string|number|null|undefined} */
  value
) => {
  const result = inputForceUTC0 ?
    moment.utc(value, inputFormat) :
    moment(value, inputFormat)

  if (additional)
    result.add(additional[0], additional[1])

  if (outputConvertToLocal)
    return result.local().format(outputFormat)

  return result.format(outputFormat)
})
/**
 * @typedef {object} DatetimeParseDateObjectOptions
 * @property {string} [outputFormat=undefined] undefined → ISO 8601
 * @property {boolean} [outputConvertToLocal=false] convert output value
 *    datetime to the local timezone/UTC offset - see [1]
 * @property {[import('moment/min/moment-with-locales').DurationInputArg1, import('moment/min/moment-with-locales').DurationInputArg2]} [additional] eg. [5, 'days'] - see [2]
 *
 * @link https://momentjs.com/docs/#/manipulating/local/ [1]
 * @link https://momentjs.com/docs/#/manipulating/add/ [2]
 *
 * @name parseDateObject
 * @desc Same as `parseDatetime` but with a native `Date` as input
 * @curried
 * @type {import('./datetime_').curry_parseDateObject}
 */
export const parseDateObject = curry((
  /** @type {DatetimeParseDateObjectOptions} */
  {
    outputFormat = undefined,
    outputConvertToLocal = false,
    additional = undefined,
  },
  /** @type {Date|number} */
  date
) => {
  const result = moment(date)

  if (additional)
    result.add(additional[0], additional[1])

  if (outputConvertToLocal)
    return result.local().format(outputFormat)

  return result.format(outputFormat)
})
/**
 * @typedef {object} DatetimeToDateObjectOptions
 * @property {string} [inputFormat=undefined] undefined → ISO 8601
 * @property {boolean} [inputForceUTC0=false] force input value to be
 *    used as if it was an UTC:0 value **EVEN IF** the input format does not
 *    contain any information about timezone/UTC offset - see [1]
 *
 * @link https://momentjs.com/docs/#/parsing/utc/ [1]
 *
 * @name datetimeToDateObject
 * @desc Provide a stringified datetime, returns a native `Date`
 * @curried
 * @type {import('./datetime_').curry_datetimeToDateObject}
 */
export const datetimeToDateObject = curry((
  /** @type {DatetimeParseDatetimeOptions} */
  {
    inputFormat = undefined,
    inputForceUTC0 = false,
  },
  /** @type {string|number|undefined|null} */
  value
) => {
  const result = inputForceUTC0 ?
    moment.utc(value, inputFormat) :
    moment(value, inputFormat)

  return result.toDate()
})
/**
 * @typedef {object} DatetimeNowOptions
 * @property {string} [outputFormat=undefined] undefined → ISO 8601
 * @property {[import('moment/min/moment-with-locales').DurationInputArg1, import('moment/min/moment-with-locales').DurationInputArg2]} [additional] eg. [5, 'days']
 *
 * @name now
 */
export const now = (
  /** @type {DatetimeNowOptions} */
  {
    outputFormat = undefined,
    additional = undefined
  }
) => {
  const result = moment.utc()

  if (additional)
    result.add(additional[0], additional[1])

  return result.format(outputFormat)
}
/**
 * @function now
 * @desc derived `now` without any options (0-arity)
 * @return {string} ISO 8601
 */
export const now_ = () => moment.utc().format()
/**
 * @typedef {object} DatetimeMinutesToTimeOptions
 * @property {string} outputFormat
 *   (!) available values
 *     <string> "HH:mm:ss" [default]
 *     <string> "HH:mm"
 *     <string> "HH[à]ss"
 *
 * @name minuteToTime
 * @curried
 * @type {import('./datetime_').curry_minutesToTime}
 */
export const minutesToTime = curry((
  /** @type {DatetimeMinutesToTimeOptions} */
  {
    outputFormat = 'HH:mm:ss'
  },
  /** @type {string|number} */
  value
) => {
  const parse = typeof value === 'string' ?
    parseInt(value, 10) :
    value

  const hours = Math.floor(parse / 60)
  const minutes = parse % 60

  const padHours = hours < 10 ? `0${hours}` : hours
  const padMinutes = minutes < 10 ? `0${minutes}` : minutes

  if (outputFormat === 'HH:mm:ss')
    return `${padHours}:${padMinutes}:00`

  if (outputFormat === 'HH:mm')
    return `${padHours}:${padMinutes}`

  return `${padHours}h${padMinutes}`
})
/**
 * @typedef {object} DatetimeTimeToMinutesOptions
 * @property {string} [inputFormat="HH:mm:ss"]
 *
 * @name timeToMinutes
 * @curried
 * @type {import('./datetime_').curry_minutesToTime}
 */
export const timeToMinutes = curry((
  /** @type {DatetimeTimeToMinutesOptions} */
  {
    inputFormat = 'HH:mm:ss'
  },
  /** @type {string} */
  value
) => {
  /** @type {import('moment/moment').unitOfTime.DurationConstructor} */
  // @ts-ignore: UNSAFE coerce, or best [1]
  // @link https://stackoverflow.com/questions/43058807/how-to-use-moment-duration-format-in-typescript#comment-106573562 [1]
  const inputFormat_ = inputFormat

  return moment.duration(value, inputFormat_).asMinutes()
})
/**
 * @function calendar
 * @param {string} value
 * @return {string} eg. "Last Monday at 2:30 AM"
 */
export function calendar(value) {
  return moment(value).calendar()
}
/**
 * @typedef {object} DatetimeDateFromNowOptions
 * @property {string} [inputFormat="YYYY-MM-DD"]
 *
 * @XXX momentjs local formatter does not work
 *      ie. no "Today" direct translation
 *      (even within `moment().creationData().locale`)
 * @link https://stackoverflow.com/questions/28241002/moment-js-include-text-in-middle-of-date-format#comment-120227077
 *
 * @XXX momentjs `fromNow()` is used with datetime, no options for days
 * @link https
://stackoverflow.com/a/55665842/6003907
 *
 * @name dateFromNow
 * @curried
 * @desc Returns eg. "in 4 months"
 * @type {import('./datetime_').curry_dateFromNow}
 */
export const dateFromNow = curry((
  /** @type {DatetimeDateFromNowOptions} */
  {
    inputFormat = 'YYYY-MM-DD'
  },
  /** @type {string|number|null|undefined} */
  value
) => {
  const date = moment(value, inputFormat)
  const now = moment.utc()

  // Case: provided date is today
  if (date.format(inputFormat) === now.format(inputFormat))
    return `aujourd'hui`

  // Case: provided date has not yet passed
  if (date.format(inputFormat) > now.format(inputFormat)) {

    const isTomorrow = date.diff(now, 'days') === 0

    if (isTomorrow)
      return `demain`

    const date_ = date.add(1, 'days')

    return date_.fromNow()
  }

  // Case: provided date has passed
  const isYesterday = date.diff(now, 'days') === -1

  if (isYesterday)
    return `hier`

  return date.add(1, 'days').fromNow()
})
/**
 * @typedef {object} DatetimeDateComparisonOptions
 * @property {string} [inputFormat="YYYY-MM-DD"] for both dates
 *
 * @XXX momentjs local formatter does not work
 *      ie. no "Today" direct translation
 *      (even within `moment().creationData().locale`)
 * @link https://stackoverflow.com/questions/28241002/moment-js-include-text-in-middle-of-date-format#comment-120227077
 *
 * @XXX momentjs `fromNow()` is used with datetime, no options for days
 * @link https
://stackoverflow.com/a/55665842/6003907
 *
 * @name dateComparison
 * @desc Returns eg. "4 days before"
 */
export const dateComparison = (
  /** @type {DatetimeDateComparisonOptions} */
  options,
  /** @type {string|number|null|undefined} */
  value,
  /** @type {DatetimeDateComparisonOptions} */
  options_,
  /** @type {string|number|null|undefined} */
  value_
) => {
  const opt = {
    inputFormat: 'YYYY-MM-DD',
    ...options
  }
  const opt_ = {
    inputFormat: 'YYYY-MM-DD',
    ...options_
  }

  const date = moment(value, opt.inputFormat)
  const date_ = moment(value_, opt_.inputFormat)

  // Case: both dates are equal
  if (date.format('YYYY-MM-DD') === date_.format('YYYY-MM-DD'))
    return `même jour`


  // Case: provided date has not yet passed
  // Case: provided date has passed
  //    ↳ editing filler words
  const str = date.from(date_)

  return /** @type {string} */ (pipe_(
    str,
    when(
      test(/^dans /),
      pipe(
        replace(/^dans /, ''),
        appendStr(' avant')
      )
    ),
    when(
      test(/^il y a /),
      pipe(
        replace(/^il y a /, ''),
        appendStr(' après')
      )
    )
  ))
}
/**
 * @typedef {object} DatetimeIsDatetimeValidOptions
 * @property {string} [inputFormat=undefined] undefined → ISO 8601
 *
 * @name isDatetimeValid
 * @curried
 * @type {import('./datetime_').curry_isDatetimeValid}
 */
export const isDatetimeValid = curry((
  /** @type {DatetimeIsDatetimeValidOptions} */
  {
    inputFormat = undefined
  },
  /** @type {any} */
  value
) => {
  // passing `true` as third argument to `moment()` enables strict-mode
  // @link https://momentjs.com/guides/#/parsing/strict-mode/
  return moment(value, inputFormat, true).isValid()
})
/**
 * @function isDatetimeGreaterThan
 * @desc convert two provided date, automatically parsed in timestamps,
 *    to check whether the first one is greater than the second
 */
export const isDatetimeGreaterThan = (
  /** @type {DatetimeParseDatetimeOptions} */
  options,
  /** @type {string} */
  value,
  /** @type {DatetimeParseDatetimeOptions} */
  options_,
  /** @type {string} */
  value_
) => {
  const ts = parseDatetime(
    {
      outputFormat: 'X',
      ...options
    },
    value
  )
  const ts_ = parseDatetime(
    {
      outputFormat: 'X',
      ...options_
    },
    value_
  )

  return gt(
    toInteger(ts),
    toInteger(ts_)
  )
}
/**
 * @function isDatetimeLowerThan
 * @desc convert two provided date, automatically parsed in timestamps,
 *    to check whether the first one is lower than the second
 */
export const isDatetimeLowerThan = (
  /** @type {DatetimeParseDatetimeOptions} */
  options,
  /** @type {string} */
  value,
  /** @type {DatetimeParseDatetimeOptions} */
  options_,
  /** @type {string} */
  value_
) => {
  const ts = parseDatetime(
    {
      outputFormat: 'X',
      ...options
    },
    value
  )
  const ts_ = parseDatetime(
    {
      outputFormat: 'X',
      ...options_
    },
    value_
  )

  return lt(
    toInteger(ts),
    toInteger(ts_)
  )
}
/**
 * @typedef {object} DatetimeDateDifferenceOptions
 * @property {string} [inputFormat="YYYY-MM-DD"]
 *
 * @name dateDifference
 */
export const dateDifference = (
  /** @type {"days"|"months"|"years"} */
  granularity = 'days',
  /** @type {DatetimeDateDifferenceOptions} */
  options,
  /** @type {string} */
  value,
  /** @type {DatetimeDateDifferenceOptions} */
  options_,
  /** @type {string} */
  value_,
) => {
  const opt = {
    inputFormat: 'YYYY-MM-DD',
    ...options
  }
  const opt_ = {
    inputFormat: 'YYYY-MM-DD',
    ...options_
  }

  const date = moment(value, opt.inputFormat)
  const date_ = moment(value_, opt_.inputFormat)

  return date.diff(date_, granularity)
}
/**
 * @typedef {object} DatetimeGetYearISOWeekCountOptions
 * @property {string} [inputFormat="YYYY-MM-DD"]
 * @desc Returns whether the year is `52` or a `53` weeks
 * @name getYearISOWeekCount
 * @curried
 * @type {import('./datetime_').curry_getYearISOWeekCount}
 */
export const getYearISOWeekCount = curry((
  /** @type {DatetimeGetYearISOWeekCountOptions} */
  {
    inputFormat = 'YYYY-MM-DD'
  },
  /** @type {string} */
  value
) => moment(value, inputFormat).isoWeeksInYear())

/**
 * @desc
 *  Calibrates ISO week numbers at year boundaries to keep dates within
 *  their display month.
 *  When ISO weeks span year boundaries, this function reassigns week numbers
 *  to ensure chronological display order within a calendar month view.
 *
 *  Some dates at the end of December can be assigned to Week 1 of the
 *  following year, and some dates at the beginning of January can be assigned
 *  to the last week (Week 52 or 53) of the previous year.
 *
 * @param {string} date - Date string (e.g., "2025-12-31")
 * @param {string} inputFormat - The format of the date string (e.g., "YYYY-MM-DD")
 * @param {string} weekNumber - The ISO week number as a string (e.g., "1", "52", "53")
 * @returns {string} The calibrated week number
 *
 * @example
 * // December 29-31, 2025 are in ISO week 1 of 2026, reassign to "99" for display
 * calibrateOverlapingWeek("2025-12-29", "YYYY-MM-DD", "1") // => "99"
 *
 * // January 1-4, 2026 might be in ISO week 53 of 2025, reassign to "0" for display
 * calibrateOverlapingWeek("2026-01-01", "YYYY-MM-DD", "53") // => "0"
 */
export const calibrateOverlapingWeek = (
  date,
  inputFormat,
  weekNumber,
) => {
  const isDecember = equals(
    '12',
    parseDatetime(
      {
        inputFormat,
        outputFormat: 'M'
      },
      date
    ),
  )

  const isJanuary = equals(
    '1',
     parseDatetime(
      {
        inputFormat,
        outputFormat: 'M'
      },
      date
    ),
  )

  switch (true) {

    case isDecember: {
      switch (weekNumber) {
        case '1': return '99'
        default: return weekNumber
      }
    }

    case isJanuary: {
      switch (weekNumber) {
        case '52':
        case '53':
          return '0'
        default:
          return weekNumber
      }
    }

    default:
      return weekNumber
  }
}

/// ==========================================
///   Entities/uDaMois
/// ==========================================

/**
 * @desc
 *    From `uDaMois_E` values
 *
 * @enum {typeof MONTH_LIST[keyof typeof MONTH_LIST]}
 * @readonly
 */
export const MONTH_LIST = /** @type {const} */ ({
  JANUARY:    1,
  FEBRUARY:   2,
  MARCH:      3,
  APRIL:      4,
  MAY:        5,
  JUNE:       6,
  JULY:       7,
  AUGUST:     8,
  SEPTEMBER:  9,
  OCTOBER:    10,
  NOVEMBER:   11,
  DECEMBER:   12,
})
/**
 * @desc Print string of `uDaMois_E` values
 * @param {MONTH_LIST} value
 * @return {string}
 */
export const printMonthList = value => {
  switch (value) {
    case MONTH_LIST.JANUARY:    return 'Janvier'
    case MONTH_LIST.FEBRUARY:   return 'Février'
    case MONTH_LIST.MARCH:      return 'Mars'
    case MONTH_LIST.APRIL:      return 'Avril'
    case MONTH_LIST.MAY:        return 'Mai'
    case MONTH_LIST.JUNE:       return 'June'
    case MONTH_LIST.JULY:       return 'Juillet'
    case MONTH_LIST.AUGUST:     return 'Août'
    case MONTH_LIST.SEPTEMBER:  return 'Septembre'
    case MONTH_LIST.OCTOBER:    return 'Octobre'
    case MONTH_LIST.NOVEMBER:   return 'Novembre'
    case MONTH_LIST.DECEMBER:   return 'Décembre'
  }
}
/**
 * @desc Print string of `uDaMois_E` values for a year use case
 * @param {MONTH_LIST} value
 * @return {string}
 */
export const printMonthList_year = value => {
  switch (value) {
    case MONTH_LIST.JANUARY:    return 'Janvier à Décembre'
    case MONTH_LIST.FEBRUARY:   return 'Février à Janvier'
    case MONTH_LIST.MARCH:      return 'Mars à Février'
    case MONTH_LIST.APRIL:      return 'Avril à Mars'
    case MONTH_LIST.MAY:        return 'Mai à Avril'
    case MONTH_LIST.JUNE:       return 'Juin à Mai'
    case MONTH_LIST.JULY:       return 'Juillet à Juin'
    case MONTH_LIST.AUGUST:     return 'Août à Juillet'
    case MONTH_LIST.SEPTEMBER:  return 'Septembre à Août'
    case MONTH_LIST.OCTOBER:    return 'Octobre à Septembre'
    case MONTH_LIST.NOVEMBER:   return 'Novembre à Octobre'
    case MONTH_LIST.DECEMBER:   return 'Décembre à Novembre'
  }
}
